import React from 'react';
import { Link } from 'react-router-dom';
import { CalendarIcon, UsersIcon, TrashIcon, PencilIcon, PlusIcon } from '@heroicons/react/24/outline';
import { PlayOutput, Player, GameOutput, LeagueOutput } from '../../generated-api';
import { ReactionButton } from '../reactions';
import { resolveLeagueIcon } from '../../utils/leagueIcon';

interface PlayListProps {
  plays: PlayOutput[];
  players: Player[];
  games: GameOutput[];
  leagues?: LeagueOutput[];
  showActions?: boolean;
  maxItems?: number;
  showPlayerDetails?: boolean;
  showFullDetails?: boolean;
  showPlayImages?: boolean;
  onDeletePlay?: (playId: string) => void;
  onReactionChange?: () => void;
}

const PlayList: React.FC<PlayListProps> = ({
  plays,
  players,
  games,
  leagues = [],
  showActions = false,
  maxItems,
  showPlayerDetails = true,
  showFullDetails = true,
  showPlayImages = false,
  onDeletePlay,
  onReactionChange,
}) => {
  const sortedPlays = plays.sort((a, b) => new Date(b.playDate).getTime() - new Date(a.playDate).getTime());
  const displayPlays = maxItems ? sortedPlays.slice(0, maxItems) : sortedPlays;

  const formatPlayDate = (dateString: string) => {
    const date = new Date(dateString);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
  };

  const getPlayerName = (playerId: string) => {
    const player = players.find(p => p.id === playerId);
    return player ? player.name : `Player ${playerId.slice(-4)}`;
  };

  const getGame = (gameId: string) => {
    return games.find(g => g.id === gameId);
  };

  const getGameName = (gameId: string) => {
    const game = getGame(gameId);
    return game ? game.name : `Game ${gameId.slice(-4)}`;
  };

  const getLeagueTags = (leagueIds: string[]) => {
    return leagueIds.map(leagueId => {
      const league = leagues.find(l => l.id === leagueId);
      if (!league) return null; // Skip unresolved leagues (e.g. game-specific leagues not in the leagues list)
      if (league.isGlobal || league.isGameSpecificLeague) return null;
      const isUnranked = league.name === 'Unranked';

      return {
        id: leagueId,
        name: league.name,
        icon: league.icon,
        isUnranked: isUnranked
      };
    })
    .filter((league): league is NonNullable<typeof league> => league !== null)
    .sort((a, b) => {
      // Sort Unranked first, then alphabetically
      if (a.isUnranked) return -1;
      if (b.isUnranked) return 1;
      return a.name.localeCompare(b.name);
    });
  };

  const getTeamPlayerNames = (teams: Record<string, string[]>) => {
    return Object.entries(teams).map(([teamName, playerIds], index) => {
      const teamPlayers = playerIds.map(playerId => {
        const player = players.find(p => p.id === playerId);
        return {
          id: playerId,
          name: getPlayerName(playerId),
          player: player
        };
      });

      // Check if this is a single-player team where team name matches player name
      const isSoloTeam = teamPlayers.length === 1 &&
        teamPlayers[0].name.toLowerCase() === teamName.toLowerCase();

      return {
        teamName: teamName || `Team ${index + 1}`,
        players: teamPlayers,
        isSoloTeam
      };
    });
  };

  const getRankingDisplay = (ranking: (string | string[])[]) => {
    let currentPosition = 1;
    
    return ranking.map((rankEntry) => {
      if (Array.isArray(rankEntry)) {
        // Tie - multiple teams at same position
        const result = {
          position: currentPosition,
          teams: rankEntry.join(' & '),
          isTie: true
        };
        currentPosition += rankEntry.length; // Skip positions for tied teams
        return result;
      } else {
        // Single team
        const result = {
          position: currentPosition,
          teams: rankEntry,
          isTie: false
        };
        currentPosition += 1; // Move to next position
        return result;
      }
    });
  };

  if (plays.length === 0) {
    return (
      <div className="bg-surface shadow rounded-xl p-12 text-center">
        <CalendarIcon className="mx-auto h-12 w-12 text-disabled" />
        <h3 className="mt-2 text-sm font-medium text-heading">No play sessions recorded</h3>
        <p className="mt-1 text-sm text-muted">Get started by recording your first play session.</p>
        <div className="mt-6">
          <Link
            data-track="play_new"
            to="/plays/new"
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-700 hover:bg-primary-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Record Play Session
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-surface shadow overflow-hidden sm:rounded-md">
      <ul className="divide-y divide-edge">
        {displayPlays.map((play) => {
          const playDateTime = formatPlayDate(play.playDate);
          const teams = getTeamPlayerNames(play.teams);
          const ranking = getRankingDisplay(play.ranking);
          const gameName = getGameName(play.gameId);
          const leagueTags = getLeagueTags(play.leagueIds);

          return (
            <li key={play.id}>
              <div className="px-4 py-4 sm:px-6 hover:bg-surface-hover">
                <Link data-track="play_card" to={`/plays/${play.id}`} className="block cursor-pointer active:scale-[0.98] transition-transform">
                  <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="flex items-center min-w-0">
                        <div className="mr-4 flex items-center min-w-0">
                          {getGame(play.gameId)?.imageUrl && (
                            <img
                              src={getGame(play.gameId)?.imageUrl}
                              alt={gameName}
                              className="h-20 w-20 sm:h-24 sm:w-24 rounded-lg object-contain mr-3 flex-shrink-0"
                            />
                          )}
                          <div className="min-w-0 flex-1">
                            <h3 className="text-lg font-medium text-heading truncate">
                              {gameName}
                            </h3>
                          {play.gameExtensions && play.gameExtensions.length > 0 && showFullDetails && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {play.gameExtensions.map((extension, idx) => (
                                <span key={idx} className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                                  {extension}
                                </span>
                              ))}
                            </div>
                          )}
                          {leagueTags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {leagueTags.map((league) => (
                                <span key={league.id} className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
                                  'bg-yellow-100 text-yellow-800'
                                }`}>
                                  {league.icon && resolveLeagueIcon(league.icon, '') !== '' && <span className="mr-1">{resolveLeagueIcon(league.icon, '')}</span>}
                                  {league.name}
                                </span>
                              ))}
                            </div>
                          )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center text-sm text-muted mb-1">
                          <CalendarIcon className="h-4 w-4 mr-1" />
                          {playDateTime.date} at {playDateTime.time}
                        </div>
                        <div className="flex items-center text-sm text-muted">
                          <UsersIcon className="h-4 w-4 mr-1" />
                          {teams.length} teams, {Object.values(play.teams).flat().length} players
                        </div>
                      </div>
                    </div>
                    
                    {/* Teams and Ranking Display - Compact version */}
                    {showPlayerDetails && showFullDetails && (
                      <div className="mt-3 flex flex-wrap items-center gap-2">
                        {ranking.map((rank, index) => {
                          // Find the teams for this ranking position
                          const rankedTeams = rank.teams.split(' & ').map(teamName => {
                            const team = teams.find(t => t.teamName === teamName);
                            return { teamName, team };
                          });

                          return rankedTeams.map((rankedTeam, teamIndex) => {
                            const isSoloTeam = rankedTeam.team?.isSoloTeam;
                            const soloPlayer = isSoloTeam ? rankedTeam.team?.players[0] : null;

                            return (
                              <div
                                key={`${index}-${teamIndex}`}
                                className={`inline-flex items-center gap-2 px-2.5 py-1.5 rounded-full text-sm ${
                                  rank.position === 1
                                    ? 'bg-yellow-50 border border-yellow-200'
                                    : rank.position === 2
                                    ? 'bg-surface-subtle border border-edge'
                                    : rank.position === 3
                                    ? 'bg-orange-50 border border-orange-200'
                                    : 'bg-surface-base border border-edge'
                                }`}
                              >
                                {/* Rank badge */}
                                <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                                  rank.position === 1
                                    ? 'bg-yellow-400 text-yellow-900'
                                    : rank.position === 2
                                    ? 'bg-gray-300 text-body'
                                    : rank.position === 3
                                    ? 'bg-orange-300 text-orange-900'
                                    : 'bg-surface-skeleton text-body'
                                }`}>
                                  {rank.position}
                                </span>

                                {isSoloTeam && soloPlayer ? (
                                  /* Solo player - show avatar + name */
                                  <div className="flex items-center gap-2">
                                    {soloPlayer.player?.imageUrl ? (
                                      <img
                                        src={soloPlayer.player.imageUrl}
                                        alt={soloPlayer.name}
                                        className="h-7 w-7 rounded-full object-cover"
                                      />
                                    ) : (
                                      <div className="h-7 w-7 rounded-full bg-surface-skeleton flex items-center justify-center">
                                        <span className="text-sm text-body font-medium">
                                          {soloPlayer.name.charAt(0).toUpperCase()}
                                        </span>
                                      </div>
                                    )}
                                    <span className="text-heading font-medium">{soloPlayer.name}</span>
                                  </div>
                                ) : (
                                  /* Team - show team name with stacked player avatars */
                                  <div className="flex items-center gap-2">
                                    {/* Stacked avatars */}
                                    <div className="flex -space-x-2">
                                      {rankedTeam.team?.players.slice(0, 3).map((player, playerIndex) => (
                                        player.player?.imageUrl ? (
                                          <img
                                            key={playerIndex}
                                            src={player.player.imageUrl}
                                            alt={player.name}
                                            className="h-7 w-7 rounded-full object-cover border-2 border-white"
                                          />
                                        ) : (
                                          <div
                                            key={playerIndex}
                                            className="h-7 w-7 rounded-full bg-surface-skeleton flex items-center justify-center border-2 border-white"
                                          >
                                            <span className="text-sm text-body font-medium">
                                              {player.name.charAt(0).toUpperCase()}
                                            </span>
                                          </div>
                                        )
                                      ))}
                                      {(rankedTeam.team?.players.length || 0) > 3 && (
                                        <div className="h-7 w-7 rounded-full bg-surface-skeleton flex items-center justify-center border-2 border-white">
                                          <span className="text-xs text-body font-medium">
                                            +{(rankedTeam.team?.players.length || 0) - 3}
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                    <span className="text-heading font-medium">{rankedTeam.teamName}</span>
                                  </div>
                                )}

                                {/* Tie indicator */}
                                {rank.isTie && (
                                  <span className="text-xs text-yellow-700 font-medium">(tie)</span>
                                )}
                              </div>
                            );
                          });
                        })}
                      </div>
                    )}
                    
                    {/* Comment Display */}
                    {play.comment && showFullDetails && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-heading mb-1">Comments:</h4>
                        <p className="text-sm text-body bg-surface-base rounded-md p-2">
                          {play.comment}
                        </p>
                      </div>
                    )}
                    
                    {/* Play Session Photos */}
                    {showPlayImages && play.imageUrls && play.imageUrls.length > 0 && showFullDetails && (
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-heading mb-2">Photos:</h4>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                          {play.imageUrls.map((imageUrl, index) => (
                            <div key={index} className="relative">
                              <img
                                data-track="play_photo"
                                src={imageUrl}
                                alt={`Play session ${index + 1}`}
                                className="w-full h-32 max-w-full object-cover rounded-md border border-edge cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={() => {
                                  // Open image in new tab for full view
                                  window.open(imageUrl, '_blank');
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {showActions && onDeletePlay && (
                    <div className="ml-2 -mr-3 sm:ml-6 sm:mr-0 flex-shrink-0">
                      <div className="flex flex-col sm:gap-2">
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            window.location.href = `/plays/${play.id}/edit`;
                          }}
                          className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 rounded-md text-primary-700 hover:text-primary-900"
                          title="Edit play session"
                          aria-label="Edit play session"
                        >
                          <PencilIcon className="h-5 w-5" />
                        </button>
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            onDeletePlay(play.id);
                          }}
                          className="inline-flex items-center justify-center min-h-[44px] min-w-[44px] sm:min-h-0 sm:min-w-0 rounded-md text-red-600 hover:text-red-900"
                          title="Delete play session"
                          aria-label="Delete play session"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </Link>

              {/* Reaction Button - Outside Link to prevent navigation */}
              <div className="px-4 pb-4 sm:px-6 flex justify-end">
                <ReactionButton
                  playId={play.id}
                  reactionSummary={play.reactionSummary}
                  userReaction={play.userReaction}
                  onReactionChange={onReactionChange}
                />
              </div>
            </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default PlayList;